import { useRef, type ReactNode } from 'react'
import { useLocation, Outlet } from 'react-router-dom'
import { motion, AnimatePresence, MotionConfig, type Variants } from 'motion/react'
import { duration, easing } from '../../lib/motion'

const pageVariants: Variants = {
  initial: (dir: number) => ({ opacity: 0, x: dir * 24 }),
  animate: { opacity: 1, x: 0, transition: { duration: duration.base, ease: easing.out } },
  exit: (dir: number) => ({
    opacity: 0,
    x: dir * -24,
    transition: { duration: duration.base, ease: easing.out }
  })
}

interface AnimatedPageWrapperProps {
  /**
   * Mapa opcional pathname → profundidad. Si no matchea exacto, la profundidad
   * se deriva del número de segmentos. Útil para rutas hermanas (ej.
   * `/app/cliente/eventos` y `/app/cliente/eventos/nuevo`) que quieren que la
   * dirección del slide sea coherente con la jerarquía lógica.
   */
  depthMap?: Record<string, number>
  /**
   * Permite renderizar manualmente los children en vez de usar `<Outlet />`.
   * Por defecto envuelve el `<Outlet />` del router.
   */
  children?: ReactNode
}

/**
 * Envuelve rutas con transición slide+fade direction-aware.
 *
 * Al navegar a una ruta con mayor profundidad, el slide va de derecha → izquierda;
 * al "volver" (profundidad menor), de izquierda → derecha. Respeta
 * prefers-reduced-motion automáticamente (MotionConfig).
 *
 * Portado del `App.tsx` de v1 (lógica de depth + AnimatePresence mode="wait").
 */
export function AnimatedPageWrapper({ depthMap, children }: AnimatedPageWrapperProps) {
  const location = useLocation()
  const depth = getDepth(location.pathname, depthMap)
  const prevDepthRef = useRef(depth)
  const direction = depth >= prevDepthRef.current ? 1 : -1
  prevDepthRef.current = depth

  return (
    <MotionConfig reducedMotion="user">
      <AnimatePresence mode="wait" custom={direction}>
        <motion.div
          key={location.pathname}
          custom={direction}
          variants={pageVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}
        >
          {children ?? <Outlet />}
        </motion.div>
      </AnimatePresence>
    </MotionConfig>
  )
}

function getDepth(pathname: string, map?: Record<string, number>): number {
  if (map && map[pathname] !== undefined) return map[pathname]
  return pathname.split('/').filter(Boolean).length
}
